import { Grid2, Link, Stack } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Attachment from '@components/Attachments/Attachment';
import Icon from '@components/Icon/Icon';
import { PrivateImage } from '@components/Image';

import { useScanResults } from '@hooks/useScanResults';

import type { ScanResult } from '@models/scan';

import { capitalize } from '@utils/format';

import Content from '../Common/CardContent';
import SkinLesionRisk from '../Common/SkinLesionRisk';
import { determineLesionRiskLevel } from '../Common/util';

const messages = defineMessages({
  overallRisk: {
    id: 'skinScanCard.overallRisk',
    defaultMessage: 'Overall Risk:',
  },
  downloadImage: {
    id: 'skinScanCard.download',
    defaultMessage: 'Download Image',
  },
});

interface SkinScanContentProps {
  data: ScanResult;
  patientId?: string | null;
}

export default function SkinScanContent({
  data,
  patientId,
}: SkinScanContentProps) {
  const { formatMessage } = useIntl();
  const { is_sharing } = useScanResults();

  const highestRisk = data.skin_results?.overall_risk;
  const location = data.skin_lesion_location
    ? capitalize(data.skin_lesion_location)
    : null;

  const attachment = data.attachment;

  const actions =
    patientId && attachment ? (
      <Attachment
        filename={formatMessage(messages.downloadImage)}
        attachment={attachment}
        patient_id={patientId}
        render={({ attachment, handleDownload, index }) => {
          return (
            <Stack direction="row">
              <Icon
                color="text.secondary"
                name="download"
                style={{
                  fontSize: 15,
                }}
                type="fas"
              />

              <Link onClick={() => handleDownload(attachment, index)}>
                {formatMessage(messages.downloadImage)}
              </Link>
            </Stack>
          );
        }}
      />
    ) : null;

  return (
    <>
      {patientId && attachment ? (
        <PrivateImage
          alt={'Skin'}
          cssStyle={{
            objectFit: 'contain',
            width: '100%',
            height: '100%',
            animation: 'fadeIn 0.5s',
          }}
          is_sharing={is_sharing}
          width={344}
          height={245}
          patient_id={patientId}
          record_id={attachment.id ?? ''}
        />
      ) : null}
      <Content
        actions={actions}
        date={data.date}
        header={location}
        title={{
          label: formatMessage(messages.overallRisk),
          risk: determineLesionRiskLevel(highestRisk ?? ''),
          value: highestRisk ?? '',
        }}
      >
        <Grid2 container gap={3}>
          {data.skin_results?.lesions.map((lesion, index) => {
            return (
              <Grid2 key={`${data.result_id}:skin-lesion-risk-${index}`}>
                <SkinLesionRisk lesion={lesion} index={index} />
              </Grid2>
            );
          })}
        </Grid2>
      </Content>
    </>
  );
}
